import { useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import { styled } from "styled-components";

import { FilterButton, InfinityLoader, ListItemBox, LoadingCards, PlayIcon } from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { activateModal } from "domains/modal/actions";
import { TopicDetailSection } from "domains/topicDetail/components/TopicDetailSection";
import { TopicSearchBar } from "domains/topicSearchFilter/components/TopicSearchBar";
import { getContentHasFilters } from "domains/topicSearchFilter/selectors";
import { getTmSelectedId, getTmSelectedOuterNode } from "domains/transformationMap/selectors";
import { applyVideoSearchCriteria } from "domains/videos/actions";
import { SmallVideoItem } from "domains/videos/components/SmallVideoItem";
import {
  createVideosForTopicSelector,
  getIsVideosFiltered,
  getVideoSearchTerm,
  getVideosFilterCount,
} from "domains/videos/selectors";
import { createKnowledgesSelector } from "entities/knowledge/selectors";
import { getCanAccessTopicVideos, getCanSeeSectionContent } from "entities/permissions/selectors";
import { createHasUnauthenticatedContentAccessSelector } from "entities/topics/selectors";

const StyledRow = styled.div`
  display: flex;
`;

const StyledFilterSearchRow = styled(StyledRow)`
  margin-bottom: 1rem;
`;

const StyledSearch = styled(TopicSearchBar)`
  margin: 0;
`;

const StyledCard = styled(ListItemBox)`
  padding: 1rem;
`;

const StyledSmallPlayIcon = styled(PlayIcon)`
  width: 42px;
  height: 42px;
  color: white;
`;

type VideoSectionProps = { selectedSectionId?: string; selectSectionHandle: (id: string) => void; topicId: string };

const SECTION_ID = "videos";
const ITEM_LIMIT = 3;

export const VideoSection = ({ topicId, selectedSectionId, selectSectionHandle }: VideoSectionProps) => {
  const isExpanded = SECTION_ID === selectedSectionId;

  const dispatch = useDispatch();
  const location = useLocation();
  const unauthenticatedSectionAccess = useSelector(createHasUnauthenticatedContentAccessSelector(topicId));
  const hasAccess = useSelector(getCanAccessTopicVideos);
  const canSeeSection = useSelector(getCanSeeSectionContent);
  const hasFilters = useSelector(getContentHasFilters("videos"));
  const isFiltered = useSelector(getIsVideosFiltered);
  const selectedInnerNodeId = useSelector(getTmSelectedId);
  const selectedOuterNode = useSelector(getTmSelectedOuterNode);
  const filterCount = useSelector(getVideosFilterCount);
  const searchTerm = useSelector(getVideoSearchTerm);
  const translate = useTranslate();

  const id = selectedInnerNodeId ?? topicId;

  const videosForTopic = useSelector(createVideosForTopicSelector(id));

  const knowledgesSelector = useMemo(
    () => createKnowledgesSelector(videosForTopic?.data ?? []),
    [videosForTopic?.data],
  );

  const videos = useSelector(knowledgesSelector).slice(0, isExpanded ? undefined : ITEM_LIMIT);

  const hasNextPage = videosForTopic?.hasNextPage ?? false;
  const isPending = videosForTopic?.isFetching ?? false;

  useEffect(() => {
    if (id && videos.length === 0) {
      dispatch(applyVideoSearchCriteria());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (id && selectedOuterNode?.id) {
      dispatch(applyVideoSearchCriteria());
    }
  }, [dispatch, id, selectedOuterNode?.id]);

  const isLocked = !unauthenticatedSectionAccess && hasAccess && !canSeeSection;

  const hide = !canSeeSection && !hasAccess;

  const path = `${location.pathname}/videos`;

  const loadNextPage = () => {
    dispatch(
      applyVideoSearchCriteria({
        offset: videos.length,
        limit: 20,
      }),
    );
  };

  const onFilterClick = () => dispatch(activateModal({ id: "TopicVideoFilter", params: { topicId } }));

  const onSearchHandle = (search: string) => dispatch(applyVideoSearchCriteria({ search }));

  const onSelection = () => selectSectionHandle(SECTION_ID);

  const isHidden = selectedSectionId !== SECTION_ID && !!selectedSectionId;

  if (videos.length === 0 && !isPending && !isFiltered) {
    return null;
  }

  return (
    <TopicDetailSection
      title={translate("app.discover.topic.tabs.videos.title")}
      display={!hide}
      isExpanded={isExpanded}
      isHidden={isHidden}
      selectSectionHandle={onSelection}
      isLocked={isLocked}
      isPending={isPending}
      filterCount={filterCount}
      hasNextPage={videos.length >= ITEM_LIMIT}
      topicId={topicId}
    >
      {isExpanded && (
        <StyledFilterSearchRow>
          <StyledSearch onSearch={onSearchHandle} isPending={isPending} defaultValue={searchTerm} />
          {hasFilters && <FilterButton count={filterCount} onClick={onFilterClick} />}
        </StyledFilterSearchRow>
      )}
      <InfinityLoader loadNextPage={loadNextPage} hasNextPage={isExpanded && hasNextPage} isPending={isPending}>
        {videos.map((video) => (
          <ListItemBox key={video.id}>
            <SmallVideoItem key={video.id} knowledge={video} link={`${path}/${video.id}`} />
          </ListItemBox>
        ))}
        {isPending && videos.length === 0 && !isHidden && (
          <LoadingCards
            cardHeight={115}
            displayDetailButton
            imageHeight={90}
            imageIcon={<StyledSmallPlayIcon />}
            imageWidth={120}
            lines={[30, 100, 100, 100]}
            numberOfItems={ITEM_LIMIT}
          />
        )}
      </InfinityLoader>
      {videos.length === 0 && !isPending && isFiltered && (
        <StyledCard>{translate("app.monitor.empty_message_filter")}</StyledCard>
      )}
    </TopicDetailSection>
  );
};
